import React, { useState, useEffect } from 'react';

const MuscleAnimation = ({ 
  exercise, 
  isPlaying = false, 
  speed = 1,
  primaryColor = "#3B82F6", // Blue for primary muscles
  secondaryColor = "#10B981", // Green for secondary muscles
  outlineColor = "#374151" // Gray for body outline
}) => {
  const [animationPhase, setAnimationPhase] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Realistic human muscle groups mapping to SVG paths
  const musclePaths = {
    // Head and neck muscles
    neck: "M 47 12 Q 50 10 53 12 L 53 18 Q 50 20 47 18 Z",
    
    // Chest - Pectorals (realistic chest shape)
    chest: "M 40 18 Q 35 20 30 25 L 30 40 Q 35 45 40 40 Q 45 45 50 40 L 50 25 Q 45 20 40 18",
    
    // Shoulders - Deltoids (realistic shoulder shape)
    shoulders: "M 30 25 Q 25 20 20 25 L 15 30 Q 10 35 15 40 L 20 45 Q 25 50 30 45 Q 35 50 40 45 L 45 40 Q 50 35 45 30 L 40 25 Q 35 20 30 25",
    
    // Arms - Biceps (front of upper arm)
    biceps: "M 25 30 Q 20 25 15 30 L 10 35 Q 5 40 10 45 L 15 50 Q 20 55 25 50 Q 30 55 35 50 L 40 45 Q 45 40 40 35 L 35 30 Q 30 25 25 30",
    
    // Arms - Triceps (back of upper arm)
    triceps: "M 25 30 Q 20 35 15 30 L 10 25 Q 5 20 10 15 L 15 10 Q 20 5 25 10 Q 30 5 35 10 L 40 15 Q 45 20 40 25 L 35 30 Q 30 35 25 30",
    
    // Back - Lats (latissimus dorsi)
    lats: "M 40 18 Q 35 23 30 28 L 25 33 Q 20 38 25 43 L 30 48 Q 35 53 40 48 Q 45 53 50 48 L 55 43 Q 60 38 55 33 L 50 28 Q 45 23 40 18",
    
    // Core - Abdominals (six-pack area)
    abdominals: "M 35 35 Q 40 30 50 30 Q 60 30 65 35 L 65 50 Q 60 55 50 55 Q 40 55 35 50 Z",
    
    // Core - Obliques (side abs)
    obliques: "M 30 35 Q 25 30 20 35 L 20 50 Q 25 55 30 50 Q 35 55 40 50 L 40 35 Q 35 30 30 35",
    
    // Lower body - Quadriceps (front of thigh)
    quadriceps: "M 40 55 Q 45 50 50 50 Q 55 50 60 55 L 60 70 Q 55 75 50 75 Q 45 75 40 70 Z",
    
    // Lower body - Hamstrings (back of thigh)
    hamstrings: "M 40 55 Q 45 60 50 60 Q 55 60 60 55 L 60 70 Q 55 75 50 75 Q 45 75 40 70",
    
    // Glutes (buttocks)
    glutes: "M 35 50 Q 30 45 25 50 L 25 60 Q 30 65 35 60 Q 40 65 45 60 L 45 50 Q 40 45 35 50",
    
    // Calves
    calves: "M 40 75 Q 45 70 50 70 Q 55 70 60 75 L 60 90 Q 55 95 50 95 Q 45 95 40 90 Z",
    
    // Additional muscle groups for more comprehensive coverage
    // Upper back muscles
    upper_back: "M 40 18 Q 35 23 30 28 L 25 33 Q 20 38 25 43 L 30 48 Q 35 53 40 48 Q 45 53 50 48 L 55 43 Q 60 38 55 33 L 50 28 Q 45 23 40 18",
    
    // Trapezius (traps)
    traps: "M 30 20 Q 25 15 20 20 L 15 25 Q 10 30 15 35 L 20 40 Q 25 45 30 40 Q 35 45 40 40 L 45 35 Q 50 30 45 25 L 40 20 Q 35 15 30 20",
    
    // Forearms
    forearms: "M 20 40 Q 15 35 10 40 L 10 50 Q 15 55 20 50 Q 25 55 30 50 L 30 40 Q 25 35 20 40",
    
    // Hip flexors
    hip_flexors: "M 40 50 Q 45 45 50 45 Q 55 45 60 50 L 60 60 Q 55 65 50 65 Q 45 65 40 60 Z"
  };

  // Animation phases for different exercise types
  const getAnimationPhases = (exerciseName) => {
    const phases = {
      '3/4 Sit-Up': [
        { phase: 0, description: "Starting position - lying down" },
        { phase: 0.3, description: "Initial contraction - abs engaging" },
        { phase: 0.6, description: "Mid-movement - torso lifting" },
        { phase: 1, description: "Top position - torso perpendicular" },
        { phase: 0.6, description: "Returning - 3/4 down" },
        { phase: 0.3, description: "Controlled descent" },
        { phase: 0, description: "Back to start" }
      ],
      '90/90 Hamstring': [
        { phase: 0, description: "Starting position - leg at 90 degrees" },
        { phase: 0.2, description: "Initial leg movement" },
        { phase: 0.5, description: "Mid-extension" },
        { phase: 1, description: "Full extension" },
        { phase: 0.5, description: "Controlled return" },
        { phase: 0.2, description: "Final descent" },
        { phase: 0, description: "Back to start" }
      ],
      'Bulgarian Split Squat': [
        { phase: 0, description: "Starting position - standing" },
        { phase: 0.2, description: "Initial descent" },
        { phase: 0.5, description: "Mid-squat position" },
        { phase: 1, description: "Deep squat - maximum engagement" },
        { phase: 0.5, description: "Rising from squat" },
        { phase: 0.2, description: "Near standing position" },
        { phase: 0, description: "Back to start" }
      ]
    };
    
    return phases[exerciseName] || [
      { phase: 0, description: "Starting position" },
      { phase: 0.3, description: "Initial movement" },
      { phase: 0.7, description: "Mid-movement" },
      { phase: 1, description: "Peak activation" },
      { phase: 0.7, description: "Returning" },
      { phase: 0.3, description: "Near start" },
      { phase: 0, description: "Back to start" }
    ];
  };

  const animationPhases = getAnimationPhases(exercise.name);

  useEffect(() => {
    if (isPlaying && !isAnimating) {
      setIsAnimating(true);
      let currentPhase = 0;
      
      const animate = () => {
        if (currentPhase < animationPhases.length) {
          setAnimationPhase(animationPhases[currentPhase].phase);
          currentPhase++;
          setTimeout(animate, 1000 / speed);
        } else {
          setIsAnimating(false);
          setAnimationPhase(0);
        }
      };
      
      animate();
    }
  }, [isPlaying, speed, animationPhases.length]);

  // Muscle name mapping from exercise data to SVG paths
  const muscleNameMapping = {
    'abdominals': 'abdominals',
    'obliques': 'obliques',
    'chest': 'chest',
    'shoulders': 'shoulders',
    'biceps': 'biceps',
    'triceps': 'triceps',
    'lats': 'lats',
    'quadriceps': 'quadriceps',
    'hamstrings': 'hamstrings',
    'glutes': 'glutes',
    'calves': 'calves',
    'neck': 'neck',
    'middle_back': 'upper_back',
    'lower_back': 'lats',
    'hip_flexors': 'hip_flexors',
    'upper_back': 'upper_back',
    'traps': 'traps',
    'forearms': 'forearms',
    'upper_arms': 'biceps'
  };

  // Get muscle activation based on animation phase
  const getMuscleActivation = (muscle, phase) => {
    const primaryMuscles = exercise.primaryMuscles || [];
    const secondaryMuscles = exercise.secondaryMuscles || [];
    
    // Map muscle names to our SVG paths
    const mappedMuscle = muscleNameMapping[muscle] || muscle;
    
    if (primaryMuscles.includes(muscle)) {
      return {
        color: primaryColor,
        opacity: 0.3 + (phase * 0.7), // More activation for primary muscles
        scale: 1 + (phase * 0.1)
      };
    } else if (secondaryMuscles.includes(muscle)) {
      return {
        color: secondaryColor,
        opacity: 0.2 + (phase * 0.4), // Less activation for secondary muscles
        scale: 1 + (phase * 0.05)
      };
    }
    
    return {
      color: outlineColor,
      opacity: 0.1,
      scale: 1
    };
  };

  return (
    <div className="muscle-animation-container">
      <svg 
        width="200" 
        height="300" 
        viewBox="0 0 100 150"
        className="muscle-animation-svg"
      >
        {/* Realistic human body outline */}
        <g className="human-body">
          {/* Head - proper human head shape */}
          <ellipse
            cx="50"
            cy="8"
            rx="5"
            ry="6"
            fill="none"
            stroke={outlineColor}
            strokeWidth="2"
            opacity="0.6"
          />
          
          {/* Neck - realistic neck proportions */}
          <rect
            x="47"
            y="12"
            width="6"
            height="6"
            fill="none"
            stroke={outlineColor}
            strokeWidth="2"
            opacity="0.6"
            rx="1"
          />
          
          {/* Torso - realistic human torso */}
          <path
            d="M 40 18 Q 35 20 30 25 L 28 35 Q 25 40 28 45 L 30 55 Q 35 60 40 55 Q 45 60 50 55 L 52 45 Q 55 40 52 35 L 50 25 Q 45 20 40 18"
            fill="none"
            stroke={outlineColor}
            strokeWidth="2"
            opacity="0.6"
          />
          
          {/* Left arm - realistic arm shape */}
          <path
            d="M 30 25 Q 25 20 20 25 L 15 30 Q 10 35 15 40 L 20 45 Q 25 50 30 45 Q 35 50 40 45 L 45 40 Q 50 35 45 30 L 40 25 Q 35 20 30 25"
            fill="none"
            stroke={outlineColor}
            strokeWidth="2"
            opacity="0.6"
          />
          
          {/* Right arm - realistic arm shape */}
          <path
            d="M 70 25 Q 75 20 80 25 L 85 30 Q 90 35 85 40 L 80 45 Q 75 50 70 45 Q 65 50 60 45 L 55 40 Q 50 35 55 30 L 60 25 Q 65 20 70 25"
            fill="none"
            stroke={outlineColor}
            strokeWidth="2"
            opacity="0.6"
          />
          
          {/* Left leg - realistic leg shape */}
          <path
            d="M 45 55 Q 40 60 35 65 L 30 70 Q 25 75 30 80 L 35 85 Q 40 90 45 85 Q 50 90 55 85 L 60 80 Q 65 75 60 70 L 55 65 Q 50 60 45 55"
            fill="none"
            stroke={outlineColor}
            strokeWidth="2"
            opacity="0.6"
          />
          
          {/* Right leg - realistic leg shape */}
          <path
            d="M 55 55 Q 60 60 65 65 L 70 70 Q 75 75 70 80 L 65 85 Q 60 90 55 85 Q 50 90 45 85 L 40 80 Q 35 75 40 70 L 45 65 Q 50 60 55 55"
            fill="none"
            stroke={outlineColor}
            strokeWidth="2"
            opacity="0.6"
          />
        </g>
        
        {/* Muscles */}
        {Object.entries(musclePaths).map(([muscle, path]) => {
          const activation = getMuscleActivation(muscle, animationPhase);
          
          return (
            <g key={muscle}>
              <path
                d={path}
                fill={activation.color}
                opacity={activation.opacity}
                transform={`scale(${activation.scale}) translate(${50 - 50 * activation.scale}, ${75 - 75 * activation.scale})`}
                className="muscle-path"
                stroke={activation.color}
                strokeWidth="1"
                strokeOpacity="0.8"
              />
            </g>
          );
        })}
        
        {/* Animation indicator */}
        <circle
          cx="50"
          cy="10"
          r="3"
          fill={isAnimating ? "#EF4444" : "#6B7280"}
          className="animation-indicator"
        />
      </svg>
      
      {/* Exercise info */}
      <div className="mt-4 text-center">
        <h3 className="text-lg font-semibold">{exercise.name}</h3>
        <p className="text-sm text-gray-600">
          {animationPhases[Math.floor(animationPhase * animationPhases.length)]?.description || "Ready to start"}
        </p>
        
        {/* Muscle legend */}
        <div className="flex justify-center gap-4 mt-2 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: primaryColor }}></div>
            <span>Primary</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: secondaryColor }}></div>
            <span>Secondary</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MuscleAnimation;
